// src/pages/team.jsx
import React, { useState, useMemo } from 'react';
import ReactMarkdown from "react-markdown"; // markdown 渲染

import { User, Mail, ExternalLink, GraduationCap } from 'lucide-react';
import { getText } from '../data/content';
import { theme, combineClasses, getCardStyle, getButtonStyle } from '../theme/theme';
import Modal from '../components/Modal';

/**
 * ============================================================================
 * 页面：团队成员 - Team.jsx
 * ============================================================================
 * 说明：
 * - 这个页面用于显示团队成员的卡片列表，包括 PI 和成员。
 * - 页面分为三个部分：
 *   - 标题区：显示团队名称和副标题。
 *   - 卡片列表区：显示 PI 和成员的卡片列表。
 *   - 详情弹窗：显示选中成员的详情信息。
 */

const TeamView = ({ lang, themeMode = "dark" }) => {
  const isLight = themeMode === "light";

  // ############################################################################
  // ############## 01. DATA（内容读取） ########################################
  // ############################################################################
  const piDetails = getText('team.piDetails', lang);
  const members = getText('team.members', lang);

  // ############################################################################
  // ############## 02. STATE（交互状态） #######################################
  // ############################################################################
  const [selectedMember, setSelectedMember] = useState(null);
  const [isPIModalOpen, setIsPIModalOpen] = useState(false);

  // ############################################################################
  // ############## 03. DERIVED（将 PI 映射为“成员卡同结构”） ####################
  // ############################################################################
  /**
   * 将 PI 映射为“成员卡同结构”的对象
   */
  const piAsMember = useMemo(() => {
    return {
      // name 字段用于卡片标题与 Modal title
      name: getText('team.piTitle', lang),
      // role / focus 与 members 一致：card 面板展示字段
      role: getText('team.piRole', lang),
      focus: piDetails?.focus,
      desc: getText('team.piDesc', lang),
      // color：可给 PI 一个固定强调色；也可放到 content-team 里配置
      color: "border-red-600/70",
      details: {
        // 详情弹窗字段（与 members.details 同结构）
        bio: piDetails?.bio || '',
        education: piDetails?.education || '',
        research: piDetails?.research || '',
        publications: piDetails?.publications || '',
        email: piDetails?.email || '',
        scholar: piDetails?.scholar || '',
        website: piDetails?.website || '',
        image: piDetails?.image || ''
      },
      // 标识用于点击行为（PI 用独立 modal）
      __isPI: true
    };
  }, [lang, piDetails]);

  // ############################################################################
  // ############## 04. HELPERS（辅助函数） #####################################
  // ############################################################################
  const openMemberModal = (member) => setSelectedMember(member);

  const closeModal = () => {
    setSelectedMember(null);
    setIsPIModalOpen(false);
  };

  // ############################################################################
  // ############## 05. RENDER（页面渲染） ######################################
  // ############################################################################
  return (
    <div
      className={combineClasses(
        "min-h-screen pt-8 pb-20 px-4",
        theme.layout.container,
        theme.animations.fadeIn
      )}
    >
      {/* ############################################################################
          ############## 06. HEADER（标题区 + 招募提示） ############################
          ############################################################################ */}
      <div className="text-center mb-12">
        {/* 标题 */}
        <h2
          className={combineClasses(
            "text-4xl md:text-6xl font-bold mb-4",
            isLight ? "text-gray-900" : "text-white"
          )}
        >
          {getText('team.title', lang)}
        </h2>
        {/* 副标题 */}
        <p className={combineClasses("text-lg", isLight ? "text-gray-600" : "text-gray-400")}>
          {getText('team.subtitle', lang)}
        </p>

        {/* 招募提示 */}
        <div
          className={combineClasses(
            /**
             * [招募提示视觉区分]（第三人称客观注释）
             * - mt-6：与 subtitle 拉开距离，形成层级
             * - inline-block：让背景宽度随内容，而不是整行铺满
             * - px-4 py-2：形成“标签/提示条”感
             * - rounded-lg：柔和圆角
             * - border：轻边框增加可读性
             * - text-sm：保持信息级别低于正文
             * - backdrop-blur-sm：在深色主题下更柔和
             */
            "mt-6 inline-block px-4 py-2 rounded-lg border text-sm backdrop-blur-sm",
            isLight
              ? "bg-cyan-50 border-cyan-200 text-cyan-800"
              : "bg-cyan-400/10 border-cyan-400/20 text-cyan-300"
          
          )}
        >
          {getText('team.recruitNote', lang)}
        </div>

      </div>

      {/* ############################################################################
          ############## 07. GRID（PI + Members Roster） ############################
          ############################################################################ */}
      <div
        className={combineClasses(
          /**
           * [断点优化]（第三人称客观注释）
           * - grid-cols-1：移动端单列
           * - lg:grid-cols-2：更大一点才进入双列（避免 md 宽度下头像显得过大、卡片过挤）
           * - xl:grid-cols-3：更大一点才进入三列（让 3 列布局更“稳”）
           * - gap-6：卡片间距
           */
          "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        )}
      >


      {/* ############################################################################
            ############## 07-A. PI CARD（与成员卡同尺寸/同格式） ####################
            ############################################################################ */}
        <div
          className={combineClasses(
            "relative group border border-opacity-30 hover:border-opacity-80",
            `border ${piAsMember.color}`,
            getCardStyle(themeMode),
            // [排版调整] p-6 保持与成员一致；hover 反馈也一致
            "p-6 transition-all duration-300 hover:-translate-y-2 cursor-pointer",
            isLight ? "bg-white/80" : "bg-gray-900/80"
          )}
          onClick={() => setIsPIModalOpen(true)}
          role="button"
          aria-label={lang === 'cn' ? `打开成员详情：${piAsMember.name}` : `Open profile: ${piAsMember.name}`}
        >
          {/* 头像 + 右上角动作区（与成员卡同一结构） */}
          <div className="flex items-start justify-between mb-4">
            {/* [照片放大] Tailwind CSS 默认值0, 2, 4, 8, 12, 16, 20, 24, 32, 40, 48, 56, 64, 72, 80, 96, 104, 112, 128, 144, 160, 176, 192, 208, 224, 240, 256 */}
            <div
              className={combineClasses(
                "w-64 h-64 rounded-full flex items-center justify-center overflow-hidden",
                isLight ? "bg-gray-100 text-gray-500" : "bg-white/5 text-gray-400"
              )}
            >
              {piAsMember.details?.image ? (
                <img
                  src={piAsMember.details.image}
                  alt={piAsMember.name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.src = ""; }}
                />
              ) : (
                <User className="w-9 h-9" />
              )}
            </div>

            {/* 右上角快捷按钮：Email / Scholar（PI 多一个 website 可选） */}
            <div
              className={combineClasses(
                // [移动端常显 / md 以上 hover 才显]（第三人称客观注释）
                // - opacity-100：移动端默认可见（无 hover）
                // - md:opacity-0 + md:group-hover:opacity-100：md 及以上默认隐藏，hover 时显示
                "flex items-center gap-2 transition-opacity opacity-100 md:opacity-0 md:group-hover:opacity-100"
              )}
            >

              {piAsMember.details?.email ? (
                <button
                  className={combineClasses(
                    "p-2 rounded-lg transition-colors",
                    isLight ? "hover:bg-gray-100 text-gray-600 hover:text-gray-900" : "hover:bg-white/5 text-gray-400 hover:text-white"
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = `mailto:${piAsMember.details.email}`;
                  }}
                  aria-label={lang === 'cn' ? `给 ${piAsMember.name} 发邮件` : `Email ${piAsMember.name}`}
                  title={lang === 'cn' ? '邮件' : 'Email'}
                >
                  <Mail className="w-4 h-4" />
                </button>
              ) : null}

              {piAsMember.details?.scholar ? (
                <button
                  className={combineClasses(
                    "p-2 rounded-lg transition-colors",
                    isLight ? "hover:bg-gray-100 text-gray-600 hover:text-gray-900" : "hover:bg-white/5 text-gray-400 hover:text-white"
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(piAsMember.details.scholar, "_blank");
                  }}
                  aria-label={lang === 'cn' ? `打开 ${piAsMember.name} 的 Google Scholar` : `Open ${piAsMember.name}'s Google Scholar`}
                  title={getText('team.buttons.scholar', lang)}
                >
                  <GraduationCap className="w-4 h-4" />
                </button>
              ) : null}
            </div>
          </div>

          {/* 基本信息（与成员卡一致） */}
          <h4
            className={combineClasses(
              /**
               * [字号强化]
               * - text-2xl：姓名更醒目，便于快速扫描 roster
               * - font-bold：保持层级
               * - mb-2：与角色间距
               */
              "text-2xl font-bold mb-2",
              isLight ? "text-gray-900" : "text-white"
            )}
          >
            {piAsMember.name}
          </h4>

          <p
            className={combineClasses(
              /**
               * [字号强化]
               * - text-base：角色字号略大于原 text-sm，信息更易读
               * - font-mono：保持“标签”气质（若中文观感不佳可改为 font-semibold）
               */
              "text-cyan-400 font-mono text-base mb-2"
            )}
          >
            {piAsMember.role}
          </p>

          <div className={combineClasses("w-full h-[1px] my-3", isLight ? "bg-gray-200" : "bg-white/10")} />

          {/* 
            PI 卡片摘要区块（desc 字段）
            - 说明：此处展示 PI 的一句简介，与成员卡保证信息层级一致
            - text-base：与 Focus 字体级别统一，便于整体视觉归一
            - 双色模式切换：亮色用更深灰，暗色用浅灰
          */}
          <p
            className={combineClasses(
              "text-base",
              isLight ? "text-gray-600" : "text-gray-500"
            )}
          >
            {/* 
              - span 用于切换标题色（提升对比度）
              - desc：简介内容，data 层配置
            */}
            <span className={isLight ? "text-gray-800" : "text-gray-200"}>
              {piAsMember.desc}
            </span>
          </p>

          <div className={combineClasses("mt-4 text-xs opacity-0 group-hover:opacity-100 transition-opacity", isLight ? "text-gray-600" : "text-gray-500")}>
            {lang === 'cn' ? '点击查看详情' : 'Click for details'}
          </div>
        </div>

        {/* ############################################################################
            ############## 07-B. MEMBER CARDS（成员卡片） ############################
            ############################################################################ */}
        {Array.isArray(members) && members.map((member) => (
          <div
            key={member.name}
            className={combineClasses(
              "relative group border border-opacity-30 hover:border-opacity-80",
              `border ${member.color}`,
              getCardStyle(themeMode),
              "p-6 transition-all duration-300 hover:-translate-y-2 cursor-pointer",
              isLight ? "bg-white/80" : "bg-gray-900/80"
            )}
            onClick={() => openMemberModal(member)}
            role="button"
            aria-label={lang === 'cn' ? `打开成员详情：${member.name}` : `Open member profile: ${member.name}`}
          >
            <div className="flex items-start justify-between mb-4">
              {/* [照片放大] Tailwind CSS 默认值0, 2, 4, 8, 12, 16, 20, 24, 32, 40, 48, 56, 64, 72, 80, 96, 104, 112, 128, 144, 160, 176, 192, 208, 224, 240, 256 */}
              <div
                className={combineClasses(
                  "w-64 h-64 rounded-full flex items-center justify-center overflow-hidden",
                  isLight ? "bg-gray-100 text-gray-500" : "bg-white/5 text-gray-400"
                )}
              >
                {member.details?.image ? (
                  <img
                    src={member.details.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.src = ""; }}
                  />
                ) : (
                  <User className="w-9 h-9" />
                )}
              </div>

              <div
                className={combineClasses(
                  // [移动端常显 / md 以上 hover 才显]（第三人称客观注释）
                  // - opacity-100：移动端默认可见（无 hover）
                  // - md:opacity-0 + md:group-hover:opacity-100：md 及以上默认隐藏，hover 时显示
                  "flex items-center gap-2 transition-opacity opacity-100 md:opacity-0 md:group-hover:opacity-100"
                )}
              >

                {member.details?.email ? (
                  <button
                    className={combineClasses(
                      "p-2 rounded-lg transition-colors",
                      isLight ? "hover:bg-gray-100 text-gray-600 hover:text-gray-900" : "hover:bg-white/5 text-gray-400 hover:text-white"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.location.href = `mailto:${member.details.email}`;
                    }}
                    aria-label={lang === 'cn' ? `给 ${member.name} 发邮件` : `Email ${member.name}`}
                    title={lang === 'cn' ? '邮件' : 'Email'}
                  >
                    <Mail className="w-4 h-4" />
                  </button>
                ) : null}

                {member.details?.scholar ? (
                  <button
                    className={combineClasses(
                      "p-2 rounded-lg transition-colors",
                      isLight ? "hover:bg-gray-100 text-gray-600 hover:text-gray-900" : "hover:bg-white/5 text-gray-400 hover:text-white"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(member.details.scholar, "_blank");
                    }}
                    aria-label={lang === 'cn' ? `打开 ${member.name} 的 Google Scholar` : `Open ${member.name}'s Google Scholar`}
                    title={getText('team.buttons.scholar', lang)}
                  >
                    <GraduationCap className="w-4 h-4" />
                  </button>
                ) : null}
              </div>
            </div>

            <h4
              className={combineClasses(
                "text-2xl font-bold mb-2",
                isLight ? "text-gray-900" : "text-white"
              )}
            >
              {member.name}
            </h4>

            <p className="text-cyan-400 font-mono text-base mb-2">
              {member.role}
            </p>

            <div className={combineClasses("w-full h-[1px] my-3", isLight ? "bg-gray-200" : "bg-white/10")} />

            <p className={combineClasses("text-base", isLight ? "text-gray-600" : "text-gray-500")}>
              Focus:{" "}
              <span className={isLight ? "text-gray-800" : "text-gray-200"}>
                {member.focus}
              </span>
            </p>


            <div className={combineClasses("mt-4 text-xs opacity-0 group-hover:opacity-100 transition-opacity", isLight ? "text-gray-600" : "text-gray-500")}>
              {lang === 'cn' ? '点击查看详情' : 'Click for details'}
            </div>
          </div>
        ))}
      </div>

      {/* ############################################################################
          ############## 08. PI MODAL（PI 详情弹窗） ################################
          ############################################################################ */}
      <Modal
        isOpen={isPIModalOpen}
        onClose={closeModal}
        title={getText('team.piTitle', lang)}
        size="lg"
        themeMode={themeMode}
      >
        <div className="space-y-6">
          {/* [照片放大] Tailwind CSS 默认值0, 2, 4, 8, 12, 16, 20, 24, 32, 40, 48, 56, 64, 72, 80, 96, 104, 112, 128, 144, 160, 176, 192, 208, 224, 240, 256 */}
          {/* 【字体大小】text-sm → text-base → text-lg → text-xl 标题类：text-2xl / text-3xl 行高：leading-relaxed / leading-7 / leading-8*/}
          <div className="flex flex-col md:flex-row gap-6">
            <div
              className={combineClasses(
                "w-64 h-64 rounded-full overflow-hidden border-4 flex-shrink-0",
                isLight ? "border-gray-200" : "border-white/10"
              )}
            >
              {piDetails?.image ? (
                <img
                  src={piDetails.image}
                  alt={getText('team.piTitle', lang)}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.src = ""; }}
                />
              ) : (
                <div className={combineClasses("w-full h-full flex items-center justify-center", isLight ? "bg-gray-100" : "bg-white/5")}>
                  <User className={combineClasses("w-16 h-16", isLight ? "text-gray-500" : "text-gray-400")} />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="inline-block px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-bold tracking-widest uppercase rounded-full mb-3">
                {getText('team.piRole', lang)}
              </div>
              {/* PI 摘要（piDesc）：只在 PI 卡显示，成员卡不需要 */}
              {piAsMember.desc ? (
                <p className={combineClasses("mt-5 mb-4 text-base leading-relaxed font-bold", isLight ? "text-gray-600" : "text-gray-400")}>
                  {piAsMember.desc}
                </p>
              
              ) : null}
              {/* 联系方式 */}
              <div className="flex flex-wrap gap-4">
                {piDetails?.email ? (
                  <a
                    href={`mailto:${piDetails.email}`}
                    className={combineClasses(
                      "flex items-center gap-2 transition-colors text-sm",
                      isLight ? "text-cyan-700 hover:text-gray-900" : "text-cyan-400 hover:text-white"
                    )}
                  >
                    <Mail className="w-4 h-4" />
                    {piDetails.email}
                  </a>
                ) : null}

                {piDetails?.scholar ? (
                  <a
                    href={piDetails.scholar}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={combineClasses(
                      "flex items-center gap-2 transition-colors text-sm",
                      isLight ? "text-cyan-700 hover:text-gray-900" : "text-cyan-400 hover:text-white"
                    )}
                  >
                    <GraduationCap className="w-4 h-4" />
                    {getText('team.buttons.scholar', lang)}
                  </a>
                ) : null}

                {piDetails?.website ? (
                  <a
                    href={piDetails.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={combineClasses(
                      "flex items-center gap-2 transition-colors text-sm",
                      isLight ? "text-cyan-700 hover:text-gray-900" : "text-cyan-400 hover:text-white"
                    )}
                  >
                    <ExternalLink className="w-4 h-4" />
                    {lang === 'cn' ? '个人主页' : 'Website'}
                  </a>
                ) : null}
              </div>
            </div>
          </div>

          {piDetails ? (
          <>

            {/* Biography */}
            {piDetails.bio ? (
              <div>
                <h4 className="text-sm font-bold text-cyan-400 uppercase tracking-wider mb-2">
                  {lang === 'cn' ? '个人简介' : 'Biography'}
                </h4>
                <div className={combineClasses("prose max-w-none leading-relaxed prose-li:my-1 prose-p:my-3/4", 
                  isLight ? "text-gray-700" : "text-gray-300")}>
                  <ReactMarkdown>
                    {piDetails.bio}
                  </ReactMarkdown>
                </div>
              </div>
            ) : null}

            {/* Education */}
            {piDetails.education ? (
              <div>
                <h4 className="text-sm font-bold text-cyan-400 uppercase tracking-wider mb-2">
                  {lang === 'cn' ? '教育与研究经历' : 'Education & Research Experience'}
                </h4>
                <div className={combineClasses("prose max-w-none leading-relaxed prose-li:my-1 prose-p:my-3/4", isLight ? "text-gray-700" : "text-gray-300")}>
                  {/* 用 react-markdown 渲染 markdown 文本，而不是直接渲染字符串 */}
                  <ReactMarkdown>  
                    {piDetails.education}  
                  </ReactMarkdown>
                </div>
              </div>
            ) : null}

            {/* Research Interests */}
            {piDetails.research ? (
              <div>
                <h4 className="text-sm font-bold text-cyan-400 uppercase tracking-wider mb-2">
                  {lang === 'cn' ? '研究兴趣' : 'Research Interests'}
                </h4>
                <div className={combineClasses("prose max-w-none leading-relaxed prose-li:my-1 prose-p:my-3/4", isLight ? "text-gray-700" : "text-gray-300")}>
                  {/* 用 react-markdown 渲染 markdown 文本，而不是直接渲染字符串 */}
                  <ReactMarkdown>
                    {piDetails.research}  
                  </ReactMarkdown>
                </div>
              </div>
            ) : null}

            {/* Awards & Funding */}
            {piDetails.awardsFunding ? (
              <div>
                <h4 className="text-sm font-bold text-cyan-400 uppercase tracking-wider mb-2">
                  {lang === 'cn' ? '获奖与资助' : 'Awards & Funding'}
                </h4>
                <div className={combineClasses("prose max-w-none leading-relaxed prose-li:my-1 prose-p:my-3/4", 
                  isLight ? "text-gray-700" : "text-gray-300")}>
                  {/* 用 react-markdown 渲染 markdown 文本，而不是直接渲染字符串 */}
                  <ReactMarkdown>
                    {piDetails.awardsFunding}
                  </ReactMarkdown>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
        </div>
      </Modal>

      {/* ############################################################################
          ############## 09. MEMBER MODAL（成员详情弹窗） ############################
          ############################################################################ */}
      <Modal
        isOpen={!!selectedMember}
        onClose={closeModal}
        title={selectedMember?.name || ''}
        size="lg"
        themeMode={themeMode}
      >
        {selectedMember && selectedMember.details ? (
          <div className="space-y-6">
            {/* 照片 + 联系方式 */}
            <div className="flex flex-col md:flex-row gap-6">
              {/* [照片放大] Tailwind CSS 默认值0, 2, 4, 8, 12, 16, 20, 24, 32, 40, 48, 56, 64, 72, 80, 96, 104, 112, 128, 144, 160, 176, 192, 208, 224, 240, 256 */}
              <div
                className={combineClasses(
                  "w-64 h-64 rounded-full overflow-hidden border-4 flex-shrink-0",
                  isLight ? "border-gray-200" : "border-white/10"
                )}
              >
                {selectedMember.details.image ? (
                  <img
                    src={selectedMember.details.image}
                    alt={selectedMember.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.src = ""; }}
                  />
                ) : (
                  <div className={combineClasses("w-full h-full flex items-center justify-center", isLight ? "bg-gray-100" : "bg-white/5")}>
                    <User className={combineClasses("w-16 h-16", isLight ? "text-gray-500" : "text-gray-400")} />
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="inline-block px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-bold tracking-widest uppercase rounded-full mb-3">
                  {selectedMember.role}
                </div>

                <p className={isLight ? "text-gray-600" : "text-gray-400"}>
                  Focus: {selectedMember.focus}
                </p>

                <div className="flex flex-wrap gap-4 mt-3">
                  {selectedMember.details.email ? (
                    <a
                      href={`mailto:${selectedMember.details.email}`}
                      className={combineClasses(
                        "flex items-center gap-2 transition-colors text-sm",
                        isLight ? "text-cyan-700 hover:text-gray-900" : "text-cyan-400 hover:text-white"
                      )}
                    >
                      <Mail className="w-4 h-4" />
                      {selectedMember.details.email}
                    </a>
                  ) : null}

                  {selectedMember.details.scholar ? (
                    <a
                      href={selectedMember.details.scholar}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={combineClasses(
                        "flex items-center gap-2 transition-colors text-sm",
                        isLight ? "text-cyan-700 hover:text-gray-900" : "text-cyan-400 hover:text-white"
                      )}
                    >
                      <GraduationCap className="w-4 h-4" />
                      {getText('team.buttons.scholar', lang)}
                    </a>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Biography */}
            <div>
              <h4 className="text-sm font-bold text-cyan-400 uppercase tracking-wider mb-2">
                {lang === 'cn' ? '个人简介' : 'Biography'}
              </h4>
              <div className={combineClasses("prose max-w-none", isLight ? "text-gray-700" : "text-gray-300")}>
                <ReactMarkdown>
                  {selectedMember.details.bio}
                </ReactMarkdown>
              </div>
            </div>

            {/* Education */}
            {selectedMember.details.education ? (
              <div>
                <h4 className="text-sm font-bold text-cyan-400 uppercase tracking-wider mb-2">
                  {lang === 'cn' ? '教育背景' : 'Education'}
                </h4>
                <div className={combineClasses("prose max-w-none", isLight ? "text-gray-700" : "text-gray-300")}>
                  <ReactMarkdown>
                    {selectedMember.details.education}
                  </ReactMarkdown>
                </div>
              </div>
            ) : null}

            {/* Research */}
            {selectedMember.details.research ? (
              <div>
                <h4 className="text-sm font-bold text-cyan-400 uppercase tracking-wider mb-2">
                  {lang === 'cn' ? '研究方向' : 'Research Interests'}
                </h4>
                <div className={combineClasses("prose max-w-none", isLight ? "text-gray-700" : "text-gray-300")}>
                  <ReactMarkdown>
                    {selectedMember.details.research}
                  </ReactMarkdown>
                </div>
              </div>
            ) : null}

            {/* Publications */}
            {selectedMember.details.publications ? (
              <div>
                <h4 className="text-sm font-bold text-cyan-400 uppercase tracking-wider mb-2">
                  {lang === 'cn' ? '研究成果' : 'Publications'}
                </h4>
                <div className={combineClasses("prose max-w-none", isLight ? "text-gray-700" : "text-gray-300")}>
                  <ReactMarkdown>
                    {selectedMember.details.publications}
                  </ReactMarkdown>
                </div>
              </div>
            ) : null}

            {/* Awards & Funding */}
            {selectedMember.details.awardsFunding ? (
              <div>
                <h4 className="text-sm font-bold text-cyan-400 uppercase tracking-wider mb-2">
                  {lang === 'cn' ? '获奖与资助' : 'Awards & Funding'}
                </h4>
                <div className={combineClasses("prose max-w-none", isLight ? "text-gray-700" : "text-gray-300")}>
                  <ReactMarkdown>
                    {selectedMember.details.awardsFunding}
                  </ReactMarkdown>
                </div>
              </div>
            ) : null}

          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default TeamView;
